/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { CrudListType, CrudTemplate, CrudRemoveType, ListColumns } from '../../../organisms/crud/form-types';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import { Sale } from 'app/types/sale';
import { identity, pickBy } from 'lodash';
import { resolveAppPath } from 'app/core/utils/url-resolver';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<Sale> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    size: '100px',
    render: item => <IdCopy>{String(item.id).substring(0, 8)}</IdCopy>
  },
  {
    title: translate('common.table.columns.externalSaleId'),
    dataIndex: 'externalSaleId',
    key: 'externalSaleId',
    size: '150px',
    render: item => item.externalSaleId || '-'
  },
  {
    title: translate('common.table.columns.pharmacy'),
    dataIndex: 'pharmacy',
    key: 'pharmacy',
    size: '200px',
    render: item => item.pharmacy?.name || '-'
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '1fr',
    render: item => item.user?.name || '-'
  },
  {
    title: translate('common.table.columns.phoneNumber'),
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
    size: '150px',
    render: item => item.user?.phoneNumber || '-'
  },
  {
    title: translate('common.table.columns.provider'),
    dataIndex: 'provider',
    key: 'provider',
    size: '120px',
    render: item => item.provider || '-'
  },
  {
    title: translate('common.table.columns.vatNumber'),
    dataIndex: 'vatNumber',
    key: 'vatNumber',
    size: '120px',
    render: item => item.vatNumber || '-'
  },
  {
    title: translate('common.table.columns.status'),
    dataIndex: 'status',
    key: 'status',
    size: '120px',
    render: item => item.status
  },
  {
    title: translate('common.table.columns.isAnnulled'),
    dataIndex: 'isAnnulled',
    key: 'isAnnulled',
    size: '100px',
    render: item => translate(`common.boolean.${item.isAnnulled}`)
  },
  {
    title: translate('common.table.columns.quantity'),
    dataIndex: 'quantity',
    key: 'quantity',
    size: '80px',
    render: item => item.lines?.reduce((sum, line) => sum + (line.quantity || 0), 0) || 0
  },
  {
    title: translate('common.table.columns.availableItems'),
    dataIndex: 'availableItems',
    key: 'availableItems',
    size: '120px',
    render: item => `${item.lines?.filter(it => !it.deletedAt).length || 0} / ${item.lines?.length || 0}`
  },
  {
    title: translate('common.table.columns.integrationCreatedAt'),
    dataIndex: 'integrationCreatedAt',
    key: 'integrationCreatedAt',
    size: '180px',
    render: item => (item.integrationCreatedAt ? <DateTime date={item.integrationCreatedAt} /> : '-')
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '180px',
    render: item => <DateTime date={item.createdAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'status',
    inline: true,
    label: translate('common.labels.status'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.labels.created'),
        value: 'created'
      },
      {
        label: translate('common.labels.pending'),
        value: 'pending'
      },
      {
        label: translate('common.labels.processing'),
        value: 'processing'
      },
      {
        label: translate('common.labels.processed'),
        value: 'processed'
      },
      {
        label: translate('common.labels.delivered'),
        value: 'delivered'
      },
      {
        label: translate('common.labels.canceled'),
        value: 'canceled'
      }
    ]
  },
  {
    type: 'selectSearch',
    name: 'isAnnulled',
    inline: true,
    label: translate('common.labels.isAnnulled'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.boolean.true'),
        value: 'true'
      },
      {
        label: translate('common.boolean.false'),
        value: 'false'
      }
    ]
  }
];

/**
 * List.
 */

const list = (translate: TFunction): CrudListType<Sale> => ({
  columns: listColumns(translate),
  filters: listFilters(translate),
  enableExport: true,
  resolveParams: (params: any, item: any) => {
    return {
      ...params,
      id: item?.id
    };
  },
  normalizeFilters: (data: any) => {
    const { ...filters } = data.filters;
    const result: any = {
      filters: pickBy(filters, identity),
      order: [
        {
          key: 'createdAt',
          direction: 'DESC'
        }
      ]
    };

    return result;
  },
  clickLine: (item, { navigate }) => {
    const route = resolveAppPath(appRoutes.dashboard.services.salesDetail, {
      id: item.id
    });

    navigate(route, { state: { sale: item } });
  },
  route: appRoutes.dashboard.services.sales,
  canEdit: false,
  canRemove: true,
  key: ['sales'],
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  endpoint: apiEndpoints.sales,
  endpointById: apiEndpoints.salesById
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType<Sale> => ({
  endpoint: apiEndpoints.salesById
});

/**
 * Config.
 */

export function createSalesTemplate(translate: TFunction): CrudTemplate<Sale> {
  return {
    list: list(translate),
    remove: remove()
  };
}
